import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../environments/environment';

interface DashboardEntry {
  id: number;
  dashboard_title: string;
  uuid: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class SupersetService {
  private http = inject(HttpClient);
  private keycloak = inject(KeycloakService);
  private dashboardsPromise: Promise<DashboardEntry[]> | null = null;

  /**
   * Superset regenerates a dashboard's embed UUID whenever embedding is
   * reconfigured, so the numeric dashboard id (stable) must be resolved to
   * the current UUID at load time instead of storing the UUID in config.
   */
  async resolveEmbedUuid(dashboardId: string): Promise<string> {
    const dashboards = await this.listDashboards();
    const match = dashboards.find((dashboard) => String(dashboard.id) === dashboardId);
    if (!match?.uuid) {
      throw new Error(`No active embed configuration found for dashboard ${dashboardId}`);
    }
    return match.uuid;
  }

  private listDashboards(): Promise<DashboardEntry[]> {
    if (!this.dashboardsPromise) {
      this.dashboardsPromise = this.fetchDashboards().catch((error) => {
        this.dashboardsPromise = null;
        throw error;
      });
    }
    return this.dashboardsPromise;
  }

  private async fetchDashboards(): Promise<DashboardEntry[]> {
    const accessToken = await this.getAccessToken();
    const guestTokenEndpoint = environment.supersetGuestTokenEndpoint?.trim();
    if (!guestTokenEndpoint) {
      throw new Error('Superset guest token endpoint is not configured');
    }
    const dashboardsEndpoint = guestTokenEndpoint.replace(/\/middleware\/guest_token\/?$/, '/dashboards');

    return firstValueFrom(
      this.http.get<DashboardEntry[]>(dashboardsEndpoint, {
        headers: new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
        }),
        withCredentials: true,
      })
    );
  }

  async getGuestToken(): Promise<string> {
    const accessToken = await this.getAccessToken();
    const endpoint = environment.supersetGuestTokenEndpoint?.trim();
    if (!endpoint) {
      throw new Error('Superset guest token endpoint is not configured');
    }

    const response = await firstValueFrom(
      this.http.post(endpoint, {}, {
        headers: new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        responseType: 'text',
        withCredentials: true,
      })
    );

    const token = this.extractToken(response);
    if (!token) {
      throw new Error('Guest token missing in middleware response');
    }
    return token;
  }

  private async getAccessToken(): Promise<string> {
    const keycloakInstance = this.keycloak.getKeycloakInstance();
    if (!keycloakInstance.authenticated || !keycloakInstance.token) {
      throw new Error('User is not authenticated');
    }

    if (keycloakInstance.isTokenExpired()) {
      await keycloakInstance.updateToken(30);
    }

    if (!keycloakInstance.token) {
      throw new Error('Unable to obtain access token');
    }

    return keycloakInstance.token;
  }

  private extractToken(response: string): string {
    const trimmed = response.trim();
    if (!trimmed) {
      return '';
    }

    if (trimmed.startsWith('{')) {
      try {
        const payload = JSON.parse(trimmed) as Record<string, unknown>;
        const token = payload['token'] ?? payload['guest_token'];
        if (typeof token === 'string') {
          return token;
        }
        if (payload['result'] && typeof payload['result'] === 'object') {
          const result = payload['result'] as Record<string, unknown>;
          if (typeof result['token'] === 'string') {
            return result['token'];
          }
          if (typeof result['guest_token'] === 'string') {
            return result['guest_token'];
          }
        }
      } catch {
        return '';
      }
    }

    return trimmed.replace(/^["']|["']$/g, '');
  }
}
